import { useCallback, useState } from 'react';

import { api } from '../../../lib/api-client';

import type { ConversationTakeoverResult } from '../types';

/** Piso de UI (mais estrito que o backend ≥10) — coerente com os motivos da Tela 2. */
export const TAKEOVER_REASON_FLOOR = 20;

export interface UseTakeoverActions {
  /** Devolve a conversa ao atendente original. returnReason ≥10 backend (UI ≥20). */
  returnToAttendant: (returnReason: string) => Promise<ConversationTakeoverResult | null>;
  isReturning: boolean;
  returnError: unknown;
  returnResult: ConversationTakeoverResult | null;

  /** Encaminha a outro atendente. targetUserId real + transferReason ≥10 backend (UI ≥20). */
  transfer: (
    targetUserId: string,
    transferReason: string,
  ) => Promise<ConversationTakeoverResult | null>;
  isTransferring: boolean;
  transferError: unknown;
  transferResult: ConversationTakeoverResult | null;
}

/**
 * T3-1 (Tela 3 Intervenção) — lifecycle do takeover (return/transfer, REAIS).
 * Espelha `useForceActions` (D8 da Tela 2): guard anti duplo-submit (`isPending`),
 * expõe `error` (async não engole falha) e o resultado vem da RESPOSTA do servidor
 * (nunca string hardcoded). `takeoverId` ausente (deep-link sem state — D1) → no-op.
 */
export function useTakeoverActions(takeoverId: string | undefined): UseTakeoverActions {
  const [isReturning, setIsReturning] = useState(false);
  const [returnError, setReturnError] = useState<unknown>(null);
  const [returnResult, setReturnResult] = useState<ConversationTakeoverResult | null>(null);

  const [isTransferring, setIsTransferring] = useState(false);
  const [transferError, setTransferError] = useState<unknown>(null);
  const [transferResult, setTransferResult] = useState<ConversationTakeoverResult | null>(null);

  const returnToAttendant = useCallback(
    async (returnReason: string): Promise<ConversationTakeoverResult | null> => {
      const trimmed = returnReason.trim();
      if (takeoverId === undefined || trimmed.length === 0 || isReturning) return null;
      setIsReturning(true);
      setReturnError(null);
      try {
        const result = await api.takeoverReturn(takeoverId, { returnReason: trimmed });
        setReturnResult(result);
        return result;
      } catch (err) {
        setReturnError(err);
        return null;
      } finally {
        setIsReturning(false);
      }
    },
    [takeoverId, isReturning],
  );

  const transfer = useCallback(
    async (
      targetUserId: string,
      transferReason: string,
    ): Promise<ConversationTakeoverResult | null> => {
      const trimmedReason = transferReason.trim();
      if (
        takeoverId === undefined ||
        targetUserId.length === 0 ||
        trimmedReason.length === 0 ||
        isTransferring
      ) {
        return null;
      }
      setIsTransferring(true);
      setTransferError(null);
      try {
        const result = await api.takeoverTransfer(takeoverId, {
          targetUserId,
          transferReason: trimmedReason,
        });
        setTransferResult(result);
        return result;
      } catch (err) {
        setTransferError(err);
        return null;
      } finally {
        setIsTransferring(false);
      }
    },
    [takeoverId, isTransferring],
  );

  return {
    returnToAttendant,
    isReturning,
    returnError,
    returnResult,
    transfer,
    isTransferring,
    transferError,
    transferResult,
  };
}
